import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { therapyTypes } from '../data/mockData';

const FamilyTherapyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get the family therapy data
  const familyTherapy = therapyTypes.find(therapy => therapy.id === 1);

  return (
    <div className="pt-32 pb-16">
      <div className="section-container">
        {/* Hero section */}
        <div className="flex flex-col lg:flex-row gap-12 mb-16">
          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-sharon-dark-green mb-6">
              {familyTherapy.title}
            </h1>
            <div className="text-lg text-sharon-gray mb-8 whitespace-pre-line">
              {familyTherapy.fullDescription}
            </div>
            <div className="mt-8">
              <Link to="/" className="sharon-btn">
                חזרה לדף הבית
              </Link>
            </div>
          </motion.div>

          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src={familyTherapy.imageSrc} 
                alt={familyTherapy.title} 
                className="w-full h-auto"
              />
            </div>
          </motion.div>
        </div>

        {/* Process section */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-2xl font-semibold text-sharon-dark-green mb-6">
            תהליך הטיפול המשפחתי
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-sharon-light-green rounded-full flex items-center justify-center mb-4">
                <span className="text-sharon-dark-green text-xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">פגישת היכרות</h3>
              <p className="text-sharon-gray">
                בפגישת ההיכרות נכיר את כל בני המשפחה, נקשיב לסיפור המשפחתי, ונגדיר יחד את מטרות הטיפול ואת הציפיות ההדדיות.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-sharon-light-green rounded-full flex items-center justify-center mb-4">
                <span className="text-sharon-dark-green text-xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">תהליך הטיפול</h3>
              <p className="text-sharon-gray">
                במהלך הטיפול נעבוד על זיהוי דפוסי התקשורת במשפחה, הבנת מקורות הקושי, ופיתוח דרכי התמודדות חדשות ויעילות יותר.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-sharon-light-green rounded-full flex items-center justify-center mb-4">
                <span className="text-sharon-dark-green text-xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-3">יישום והטמעה</h3>
              <p className="text-sharon-gray">
                הטיפול כולל תרגול מיומנויות ויישום כלים שנלמדו במפגשים בחיי היומיום, כדי לקדם שינוי משמעותי ביחסים המשפחתיים.
              </p>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h2 className="text-2xl font-semibold text-sharon-dark-green mb-6">
            שאלות נפוצות
          </h2>
          
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-2">
                האם כל בני המשפחה צריכים להשתתף בטיפול?
              </h3>
              <p className="text-sharon-gray">
                אידיאלית, השתתפות כל בני המשפחה הרלוונטיים מועילה מאוד, אך הרכב המשתתפים יכול להשתנות בהתאם לצורך ולמטרות הטיפול.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-2">
                כמה זמן נמשך טיפול משפחתי?
              </h3>
              <p className="text-sharon-gray">
                משך הטיפול משתנה בהתאם לצרכים ולמורכבות המצב. בדרך כלל מדובר בתהליך של מספר חודשים, עם מפגשים שבועיים או דו-שבועיים.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold text-sharon-dark-green mb-2">
                האם טיפול משפחתי מתאים גם למשפחות עם ילדים קטנים?
              </h3>
              <p className="text-sharon-gray">
                כן, הטיפול מותאם לגיל הילדים ולמבנה המשפחתי. עבור משפחות עם ילדים קטנים, אשלב טכניקות מותאמות כמו משחק ויצירה.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          className="mt-16 bg-sharon-light-green p-8 rounded-lg text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h2 className="text-2xl font-semibold text-sharon-dark-green mb-4">
            מעוניינים לקבוע פגישת היכרות?
          </h2>
          <p className="text-lg text-sharon-gray mb-6">
            אני כאן כדי לענות על כל שאלה ולסייע לכם בתהליך. צרו איתי קשר ונתחיל יחד במסע לשיפור היחסים המשפחתיים.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 sm:space-x-reverse">
            <a
              href="tel:+972526507776"
              className="sharon-btn text-lg px-6 py-2 rounded-md"
            >
              התקשרו עכשיו: 0526507776
            </a>
            <a
              href="mailto:sharon.weisfeld@gmail.com"
              className="text-sharon-dark-green hover:text-sharon-green transition-colors duration-300 text-lg"
            >
              שלחו מייל
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FamilyTherapyPage;